/**
 * Admin Lost & Found Tab - Manage lost and found cat reports
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Cat, Eye, Trash2, CheckCircle, X, AlertTriangle, MapPin, Calendar, Gift } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminLostFoundTab = () => {
  const { lostFoundReports, lostFoundStats, loadData } = useAdmin();
  
  const [filter, setFilter] = useState("all");
  const [viewingReport, setViewingReport] = useState(null);

  const filteredReports = lostFoundReports.filter(r => {
    if (filter === "lost") return r.report_type === "lost";
    if (filter === "found") return r.report_type === "found";
    if (filter === "active") return r.status === "active";
    if (filter === "resolved") return r.status === "resolved";
    return true;
  });

  const handleUpdateStatus = async (reportId, status) => {
    try {
      await axios.put(`${API}/lost-found/${reportId}/status`, { status }, { withCredentials: true });
      toast.success(`Report marked as ${status}`);
      loadData();
      setViewingReport(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDelete = async (reportId) => {
    if (!confirm("Delete this report permanently?")) return;
    try {
      await axios.delete(`${API}/lost-found/${reportId}`, { withCredentials: true });
      toast.success("Report deleted");
      loadData();
      setViewingReport(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete report");
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{lostFoundStats?.active_lost || 0}</p>
            <p className="text-xs text-stone-500">Active Lost</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{lostFoundStats?.active_found || 0}</p>
            <p className="text-xs text-stone-500">Active Found</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Search className="w-6 h-6 text-stone-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">{lostFoundStats?.total_lost || 0}</p>
            <p className="text-xs text-stone-500">Total Lost</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Cat className="w-6 h-6 text-stone-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">{lostFoundStats?.total_found || 0}</p>
            <p className="text-xs text-stone-500">Total Found</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{lostFoundStats?.resolved || 0}</p>
            <p className="text-xs text-stone-500">Resolved</p>
          </CardContent>
        </Card>
      </div>

      {/* Reports List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Lost & Found Reports</CardTitle>
            <CardDescription>Manage lost and found cat reports</CardDescription>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reports</SelectItem>
              <SelectItem value="lost">Lost Cats</SelectItem>
              <SelectItem value="found">Found Cats</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {filteredReports.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600">No reports found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReports.map(report => (
                <div 
                  key={report.report_id} 
                  className={`flex items-center gap-4 p-4 rounded-lg border-l-4 ${
                    report.report_type === "lost" 
                      ? "bg-red-50 border-l-red-500" 
                      : "bg-green-50 border-l-green-500"
                  }`}
                >
                  {/* Image */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-stone-200 flex-shrink-0">
                    {report.image_base64 ? (
                      <img 
                        src={`data:${report.image_mime_type || 'image/jpeg'};base64,${report.image_base64}`}
                        alt={report.cat_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Cat className="w-8 h-8 text-stone-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={report.report_type === "lost" ? "bg-red-600" : "bg-green-600"}>
                        {report.report_type.toUpperCase()}
                      </Badge>
                      <h4 className="font-semibold text-stone-900">{report.cat_name}</h4>
                      <Badge variant={report.status === "active" ? "default" : "secondary"}>
                        {report.status}
                      </Badge>
                      {report.reward_offered && (
                        <Badge className="bg-amber-500">
                          <Gift className="w-3 h-3 mr-1" />
                          Reward
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-600 truncate">{report.description}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-stone-500">
                      {report.last_seen_location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {report.last_seen_location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(report.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setViewingReport(report)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {report.status === "active" && (
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleUpdateStatus(report.report_id, "resolved")}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDelete(report.report_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Report Dialog */}
      <Dialog open={!!viewingReport} onOpenChange={() => setViewingReport(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Badge className={viewingReport.report_type === "lost" ? "bg-red-600" : "bg-green-600"}>
                    {viewingReport.report_type.toUpperCase()}
                  </Badge>
                  {viewingReport.cat_name}
                  <Badge variant={viewingReport.status === "active" ? "default" : "secondary"}>
                    {viewingReport.status}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {viewingReport.image_base64 && (
                  <div className="aspect-video bg-stone-100 rounded-lg overflow-hidden">
                    <img 
                      src={`data:${viewingReport.image_mime_type || 'image/jpeg'};base64,${viewingReport.image_base64}`}
                      alt={viewingReport.cat_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div>
                  <strong>Description:</strong>
                  <p className="text-stone-600 mt-1">{viewingReport.description}</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <strong>Location:</strong>
                    <p className="text-stone-600">{viewingReport.last_seen_location || "Not specified"}</p>
                  </div>
                  <div>
                    <strong>Date:</strong>
                    <p className="text-stone-600">
                      {viewingReport.last_seen_date 
                        ? new Date(viewingReport.last_seen_date).toLocaleDateString() 
                        : "Not specified"}
                    </p>
                  </div>
                </div>
                
                {viewingReport.reward_offered && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <strong className="text-amber-700">Reward Offered:</strong>
                    <p className="text-amber-800">{viewingReport.reward_offered}</p>
                  </div>
                )}
                
                <div className="p-3 bg-stone-100 rounded-lg">
                  <strong>Contact Information:</strong>
                  <div className="mt-2 space-y-1 text-sm">
                    {viewingReport.contact_name && <p>Name: {viewingReport.contact_name}</p>}
                    {viewingReport.contact_email && <p>Email: {viewingReport.contact_email}</p>}
                    {viewingReport.contact_phone && <p>Phone: {viewingReport.contact_phone}</p>}
                  </div>
                </div>
                
                <p className="text-xs text-stone-500">
                  Reported: {new Date(viewingReport.created_at).toLocaleString()}
                </p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingReport(null)}>Close</Button>
                {viewingReport.status === "active" ? (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleUpdateStatus(viewingReport.report_id, "resolved")}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Resolved
                  </Button>
                ) : (
                  <Button 
                    variant="outline"
                    onClick={() => handleUpdateStatus(viewingReport.report_id, "active")}
                  >
                    Reopen Report
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminLostFoundTab;
